import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  Layers,
  MessageSquare,
  TrendingUp,
  ArrowUpRight,
  ArrowRight,
  Clock,
  RefreshCw,
} from 'lucide-react';
import { categoriesApi, productsApi, inquiriesApi } from '@/services/api';
import type { Inquiry, InquiryStatus } from '@/types';

interface DashboardStats {
  totalProducts: number;
  totalCategories: number;
  newInquiries: number;
  totalInquiries: number;
}

const statusLabels: Record<InquiryStatus, string> = {
  NEW: 'Yeni',
  CONTACTED: 'İletişime Geçildi',
  QUALIFIED: 'Nitelikli',
  CLOSED: 'Kapandı',
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalProducts: 0,
    totalCategories: 0,
    newInquiries: 0,
    totalInquiries: 0,
  });
  const [recentInquiries, setRecentInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);

      let totalProducts = 0;
      let totalCategories = 0;
      let newInquiries = 0;
      let totalInquiries = 0;
      let inquiriesList: Inquiry[] = [];

      try {
        const productsData = await productsApi.getAdminList({ limit: 1 });
        totalProducts = productsData.meta?.total || 0;
      } catch (err) {
        console.warn('Products API error:', err);
      }

      try {
        const categories = await categoriesApi.getAll();
        totalCategories = Array.isArray(categories) ? categories.length : 0;
      } catch (err) {
        console.warn('Categories API error:', err);
      }

      try {
        const inquiryStats = await inquiriesApi.getStats();
        newInquiries = inquiryStats?.byStatus?.NEW || 0;
        totalInquiries = inquiryStats?.total || 0;
      } catch (err) {
        console.warn('Inquiry stats API error:', err);
      }

      try {
        const inquiriesData = await inquiriesApi.getAll({ limit: 5 });
        inquiriesList = inquiriesData?.data || [];
      } catch (err) {
        console.warn('Inquiries list API error:', err);
      }

      setStats({ totalProducts, totalCategories, newInquiries, totalInquiries });
      setRecentInquiries(inquiriesList);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;

    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
    });
  };

  // Skeleton loaders
  const StatSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-4 w-20 bg-gray-200 mb-3" />
      <div className="h-10 w-16 bg-gray-200" />
    </div>
  );

  const TableSkeleton = () => (
    <div className="animate-pulse space-y-4 p-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-4 w-32 bg-gray-200" />
            <div className="h-3 w-48 bg-gray-100" />
          </div>
          <div className="h-6 w-16 bg-gray-200" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-end">
        <button
          onClick={() => loadDashboardData(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:text-gray-900 border border-gray-200 hover:border-gray-300 transition-colors disabled:opacity-50 mt-4"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Yenile</span>
        </button>
      </div>

      {/* Stats Grid - Minimal Design */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200">
        {/* Products */}
        <Link
          to="/admin/products"
          className="bg-white p-6 hover:bg-gray-50 transition-colors group"
        >
          {loading ? (
            <StatSkeleton />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <Package className="w-5 h-5 text-gray-400" />
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 transition-colors" />
              </div>
              <p className="text-sm text-gray-500 font-medium">Ürünler</p>
              <p className="text-4xl font-bold text-gray-900 mt-1 tracking-tight">
                {stats.totalProducts}
              </p>
            </>
          )}
        </Link>

        {/* Categories */}
        <Link
          to="/admin/categories"
          className="bg-white p-6 hover:bg-gray-50 transition-colors group"
        >
          {loading ? (
            <StatSkeleton />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <Layers className="w-5 h-5 text-gray-400" />
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 transition-colors" />
              </div>
              <p className="text-sm text-gray-500 font-medium">Kategoriler</p>
              <p className="text-4xl font-bold text-gray-900 mt-1 tracking-tight">
                {stats.totalCategories}
              </p>
            </>
          )}
        </Link>

        {/* New Inquiries */}
        <Link
          to="/admin/inquiries?status=NEW"
          className="bg-white p-6 hover:bg-gray-50 transition-colors group"
        >
          {loading ? (
            <StatSkeleton />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <MessageSquare className="w-5 h-5 text-gray-400" />
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 transition-colors" />
              </div>
              <p className="text-sm text-gray-500 font-medium">Yeni Talepler</p>
              <p className="text-4xl font-bold text-gray-900 mt-1 tracking-tight">
                {stats.newInquiries}
              </p>
            </>
          )}
        </Link>

        {/* Total Inquiries */}
        <Link
          to="/admin/inquiries"
          className="bg-white p-6 hover:bg-gray-50 transition-colors group"
        >
          {loading ? (
            <StatSkeleton />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-5 h-5 text-gray-400" />
                <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-gray-600 transition-colors" />
              </div>
              <p className="text-sm text-gray-500 font-medium">Toplam Talep</p>
              <p className="text-4xl font-bold text-gray-900 mt-1 tracking-tight">
                {stats.totalInquiries}
              </p>
            </>
          )}
        </Link>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-5 gap-8">
        {/* Recent Inquiries - Takes 3 columns */}
        <div className="lg:col-span-3 border border-gray-200 bg-white">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Son Talepler</h2>
            <Link
              to="/admin/inquiries"
              className="text-sm text-gray-400 hover:text-gray-900 flex items-center gap-1 transition-colors"
            >
              Tümü
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {loading ? (
            <TableSkeleton />
          ) : recentInquiries.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Henüz talep bulunmuyor</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentInquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  onClick={() => navigate('/admin/inquiries')}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">
                        {inquiry.name}
                      </p>
                      <p className="text-sm text-gray-500 truncate mt-0.5">
                        {inquiry.email}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <span className="text-xs font-medium text-gray-900 px-2 py-0.5 bg-gray-100">
                        {statusLabels[inquiry.status]}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(inquiry.createdAt)}
                      </span>
                    </div>
                  </div>
                  {inquiry.message && (
                    <p className="text-sm text-gray-400 mt-2 line-clamp-1">
                      {inquiry.message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions - Takes 2 columns */}
        <div className="lg:col-span-2 space-y-4">
          {/* Action Card */}
          <div className="border border-gray-200 bg-white p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Hızlı İşlemler</h2>
            <div className="space-y-2">
              <Link
                to="/admin/products"
                className="flex items-center justify-between p-3 border border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <Package className="w-4 h-4 text-gray-400 group-hover:text-gray-900" />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    Yeni Ürün Ekle
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900" />
              </Link>

              <Link
                to="/admin/categories"
                className="flex items-center justify-between p-3 border border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <Layers className="w-4 h-4 text-gray-400 group-hover:text-gray-900" />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    Yeni Kategori Ekle
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900" />
              </Link>

              <Link
                to="/admin/inquiries"
                className="flex items-center justify-between p-3 border border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-4 h-4 text-gray-400 group-hover:text-gray-900" />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    Talepleri Görüntüle
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900" />
              </Link>
            </div>
          </div>

          {/* Summary Card */}
          <div className="border border-gray-200 bg-white p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Özet</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Aktif Ürünler</span>
                <span className="text-sm font-semibold text-gray-900">
                  {stats.totalProducts}
                </span>
              </div>
              <div className="h-px bg-gray-100" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Kategoriler</span>
                <span className="text-sm font-semibold text-gray-900">
                  {stats.totalCategories}
                </span>
              </div>
              <div className="h-px bg-gray-100" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Bekleyen Talepler</span>
                <span className="text-sm font-semibold text-gray-900">
                  {stats.newInquiries}
                </span>
              </div>
              <div className="h-px bg-gray-100" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Toplam Talep</span>
                <span className="text-sm font-semibold text-gray-900">
                  {stats.totalInquiries}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
